import React, { useState, useCallback, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import styled from "styled-components";

import { useGetProjectQuery } from "../../../app/services/projects";
import type { ProjectDetail } from "../../../app/services/projects";
import { useGetCurrentUserQuery } from "../../../app/accounts/authuser";
import { useInitiateUploadMutation } from "../../../app/services/uploadApi";
import type { GeneratedImageCandidate } from "../../../app/services/aiImage";
import { useProjectFieldSync } from "../../../hooks/useProjectFieldSync";
import { uploadFileDirect } from "../../../utils/s3DirectUpload";
import { HOSTNAME } from "../../../constant/host";
import Tab from "../../../components/tab/Tab";
import TitleBlock from "../../../components/block/TitleBlock";
import ImagePicker from "../../../components/file-uploader/ImagePicker";
import type {
  ImagePickerChange,
  ImagePickerValue,
} from "../../../components/file-uploader/ImagePicker";
import { ASPECT_RULES } from "../../../components/image-editor/aspectRule";
import {
  base64ToFile,
  trimTransparentEdges,
  urlToDataUrl,
} from "../../../components/image-editor/cropUtils";
import AiImageGeneratorModal from "../../../components/ai-image-generator/AiImageGeneratorModal";
import RadioCard from "../../../components/form-item/RadioCard";
import StatusIndicator from "../../../components/status-indicator/StatusIndicator";
import SvgLoadingAnimation from "../../../components/loading/index";
import { fontSetting } from "../../../constant/font";
import { colorSetting } from "../../../constant/color";

/** 元画像も S3 に上げるため、single アップロードに収まる大きさに抑える */
const BANNER_MAX_BYTES = 30 * 1024 * 1024;
/** 切り抜き後の長辺の上限 px */
const BANNER_MAX_OUTPUT_EDGE = 2400;

const toAbsoluteUrl = (url: string | null | undefined): string | null =>
  url
    ? url.startsWith("http")
      ? url
      : `${HOSTNAME || window.location.origin}${url}`
    : null;

/** プロジェクト詳細からバナー画像の表示値を作る */
const toBannerValue = (
  project: ProjectDetail,
  displayUrl?: string,
): ImagePickerValue | null => {
  const url = displayUrl ?? toAbsoluteUrl(project.banner_image_optimized);
  if (!url) return null;
  return {
    url,
    sourceUrl: toAbsoluteUrl(project.banner_image_source),
    cropData: project.banner_crop_data,
  };
};

interface FontSettingItem {
  id: number | string;
  code: string;
  sample_code: string;
  active: boolean;
}

interface ColorSettingItem {
  id: number | string;
  code: string;
  active: boolean;
}

// RadioCard 全面に banner-color${n} を敷くサーフェス（padding なしで色がブロック全体に載る）。
// RadioCard の Card は position: relative なので inset: 0 でカード全体を覆う。
const CardColorSurface = styled.div`
  position: absolute;
  inset: 0;
  border-radius: 6px;
  overflow: hidden;
  display: flex;
  align-items: center;
  padding: 0 16px;
`;

// 透過ロゴを currentColor（banner-color の文字色）で塗って表示する
const LogoMask = styled.div<{ $url: string }>`
  flex: 1 1 auto;
  min-width: 0;
  height: 44px;
  background-color: currentColor;
  mask: url("${({ $url }) => $url}") left center / contain no-repeat;
  -webkit-mask: url("${({ $url }) => $url}") left center / contain no-repeat;
`;

// 再生成ボタン。banner-color の文字色を div 経由で受け取るためのラッパー。
// セレクトボタン（右上の radio）と重ならないよう右に余白を取る。
const RegenerateArea = styled.div`
  flex-shrink: 0;
  margin-left: auto;
  padding-right: 24px;
`;

// 背景透過の再生成ボタン（banner-color の文字色に追従する）。
// 角丸・padding・フォント・アイコン寸法は Button コンポーネント（small /
// outlined / width="fit"）と揃える。
const RegenerateButton = styled.button.attrs({
  className: "heading-13 u-line-height-200",
})`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 4px 12px;
  border-radius: 4px;
  border: 1px solid currentColor;
  background: transparent;
  color: inherit;
  transition: all 0.2s ease;
  white-space: nowrap;
  cursor: pointer;

  .material-symbols-rounded {
    font-size: 24px;
    margin-left: -4px;
    color: inherit;
  }
`;

const ProjectBanner: React.FC = () => {
  const { projectId = "" } = useParams<{ projectId: string }>();
  const { data: currentUserData } = useGetCurrentUserQuery();
  const { data: projectData, isLoading } = useGetProjectQuery(
    { project_id: projectId },
    { skip: !projectId || !currentUserData }
  );
  const { updateStatus, runWithStatus, saveField, updateProject } =
    useProjectFieldSync(projectId);
  const [initiateUpload] = useInitiateUploadMutation();

  const [activeTab, setActiveTab] = useState("カラーとロゴ");
  const [selectedFont, setSelectedFont] = useState("1");
  const [selectedColor, setSelectedColor] = useState("1");
  const [bannerImage, setBannerImage] = useState<ImagePickerValue | null>(null);
  const [bannerLogoUrl, setBannerLogoUrl] = useState<string | null>(null);
  const [showLogoAiModal, setShowLogoAiModal] = useState(false);
  const initializedRef = useRef(false);

  useEffect(() => {
    if (!initializedRef.current && projectData?.data?.project) {
      const p = projectData.data.project;
      setSelectedFont(p.banner_font || "1");
      setSelectedColor(p.banner_color || "1");
      setBannerImage(toBannerValue(p));
      setBannerLogoUrl(toAbsoluteUrl(p.banner_logo));
      initializedRef.current = true;
    }
  }, [projectData]);

  // S3 に purpose=project_banner でアップロードし file_key を返す
  const uploadBannerToS3 = useCallback(
    async (file: File): Promise<string> => {
      const uploaded = await uploadFileDirect(initiateUpload, file, {
        purpose: "project_banner",
        projectId,
      });
      return uploaded.fileKey;
    },
    [initiateUpload, projectId]
  );

  // 切り抜いた画像を保存する。元画像はファイル選択なら一緒にアップロードし、
  // AI 生成なら生成画像のキーをそのまま使う。再クロップでは元画像を送らない（BE が残す）。
  const handleBannerChange = useCallback(
    async ({ file, base64, cropData, source }: ImagePickerChange) => {
      try {
        await runWithStatus(async () => {
          const [croppedKey, sourceKey] = await Promise.all([
            uploadBannerToS3(file),
            source?.type === "file"
              ? uploadBannerToS3(source.file)
              : Promise.resolve(source?.type === "key" ? source.fileKey : undefined),
          ]);
          const res = await updateProject({
            project_id: projectId,
            banner_image_key: croppedKey,
            banner_crop_data: cropData,
            ...(sourceKey ? { banner_image_source_key: sourceKey } : {}),
          }).unwrap();
          // 表示は手元の切り抜き画像を使う（保存直後の URL 反映を待たない）
          setBannerImage(toBannerValue(res.data.project, base64));
        });
      } catch (error) {
        console.error("Failed to upload banner image:", error);
      }
    },
    [runWithStatus, uploadBannerToS3, updateProject, projectId]
  );

  // バナー画像を外す（元画像と切り抜き座標も BE 側で消える）
  const handleBannerRemove = useCallback(async () => {
    try {
      await runWithStatus(() =>
        updateProject({ project_id: projectId, banner_image_key: "" }).unwrap(),
      );
      setBannerImage(null);
    } catch (error) {
      console.error("Failed to remove banner image:", error);
    }
  }, [runWithStatus, updateProject, projectId]);

  // AI 生成ロゴを選択: 透過余白をトリム → PNG を S3 にアップロード →
  // banner_logo_key + banner_font='logo' を保存する。
  // 失敗は投げ直し、モーダル側でエラートーストを表示させる。
  const handleLogoGenerated = useCallback(
    async (candidate: GeneratedImageCandidate) => {
      try {
        const trimmedDataUrl = await runWithStatus(async () => {
          const dataUrl = await urlToDataUrl(candidate.url);
          // ロゴの実描画部分だけを残してアスペクト比を最適化する
          const trimmed = await trimTransparentEdges(dataUrl);
          const fileKey = await uploadBannerToS3(
            base64ToFile(trimmed, "banner-logo.png", "image/png"),
          );
          await updateProject({
            project_id: projectId,
            banner_logo_key: fileKey,
            banner_font: "logo",
          }).unwrap();
          return trimmed;
        });
        setBannerLogoUrl(trimmedDataUrl);
        setSelectedFont("logo");
        setShowLogoAiModal(false);
      } catch (error) {
        console.error("Failed to save AI banner logo:", error);
        throw error;
      }
    },
    [runWithStatus, uploadBannerToS3, updateProject, projectId]
  );

  const handleFontChange = (fontId: string) => {
    setSelectedFont(fontId);
    saveField({ banner_font: fontId });
  };

  // ロゴカード: ロゴ未生成なら生成モーダルへ、生成済みなら banner_font='logo' に切替
  const handleLogoCardClick = () => {
    if (!bannerLogoUrl) {
      setShowLogoAiModal(true);
      return;
    }
    if (selectedFont !== "logo") {
      handleFontChange("logo");
    }
  };

  const handleColorChange = (colorId: string) => {
    setSelectedColor(colorId);
    saveField({ banner_color: colorId });
  };

  const activeFonts = (fontSetting as FontSettingItem[]).filter((f) => f.active);
  const activeColors = (colorSetting as ColorSettingItem[]).filter((c) => c.active);
  const codeName = projectData?.data?.project?.code_name || "";

  if (isLoading) return <SvgLoadingAnimation />;

  return (
    <div className="u-col u-w100 u-gap16">
      <TitleBlock
        title="バナー"
        description="プロジェクトのバナーに使うカラーとロゴ・フォント、または画像を設定できます。"
      />
      <Tab
        type="tab"
        list={["カラーとロゴ", "画像アップロード"]}
        activeTab={activeTab}
        onTabChange={setActiveTab}
      />
      <div className="u-w100 c-tab1 u-col-center">
        {activeTab === "カラーとロゴ" && (
          <div className="u-w100">
            <div className="color-container u-row u-gap16">
              {activeColors
                .map((color) => (
                  <div
                    key={color.id}
                    role="button"
                    tabIndex={0}
                    aria-pressed={selectedColor === String(color.id)}
                    className={`${color.code} ${selectedColor === String(color.id) ? "is-selected" : ""}`}
                    onClick={() => handleColorChange(String(color.id))}
                    onKeyDown={(e) => e.key === "Enter" && handleColorChange(String(color.id))}
                  />
                ))}
            </div>
            <div className="u-col u-w100 u-gap16">
              {/* ロゴ画像（フォントの代替）。AI 生成した透過ロゴを banner-color で tint 表示する */}
              <RadioCard
                name="project_banner"
                id="font_logo"
                width="0"
                height="80px"
                checked={selectedFont === "logo"}
                onClick={handleLogoCardClick}
              >
                <CardColorSurface className={`banner-color${selectedColor}`}>
                  {bannerLogoUrl ? (
                    <>
                      <LogoMask
                        $url={bannerLogoUrl}
                        role="img"
                        aria-label="プロジェクトロゴ"
                      />
                      <RegenerateArea>
                        <RegenerateButton
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowLogoAiModal(true);
                          }}
                        >
                          <span className="material-symbols-rounded">
                            auto_awesome
                          </span>
                          再生成
                        </RegenerateButton>
                      </RegenerateArea>
                    </>
                  ) : (
                    <div className="u-row u-w100 u-gap8 u-align-center u-justify-center">
                      <span className="material-symbols-rounded">
                        auto_awesome
                      </span>
                      <span className="bodytext-13">
                        AIでコードネームのロゴを生成して使う
                      </span>
                    </div>
                  )}
                </CardColorSurface>
              </RadioCard>
              {activeFonts.map((font) => (
                <RadioCard
                  key={font.id}
                  name="project_banner"
                  id={`font_${String(font.id).padStart(2, "0")}`}
                  width="0"
                  height="80px"
                  checked={selectedFont === String(font.id)}
                  onClick={() => handleFontChange(String(font.id))}
                >
                  <CardColorSurface className={`banner-color${selectedColor}`}>
                    <div className={font.code} style={{ width: "100%" }}>
                      {codeName || font.sample_code}
                    </div>
                  </CardColorSurface>
                </RadioCard>
              ))}
            </div>
            <StatusIndicator status={updateStatus} />
          </div>
        )}
        {activeTab === "画像アップロード" && (
          <div className="u-col u-w100 u-gap16">
            <ImagePicker
              value={bannerImage}
              aspectRule={ASPECT_RULES.projectBanner}
              size="large"
              maxBytes={BANNER_MAX_BYTES}
              maxOutputEdge={BANNER_MAX_OUTPUT_EDGE}
              outputFileName="banner.jpg"
              aiGenerator={
                projectId ? { scene: "image_project_banner", projectId } : undefined
              }
              onChange={handleBannerChange}
              onRemove={handleBannerRemove}
            />
            <StatusIndicator status={updateStatus} />
          </div>
        )}
      </div>

      {/* AI ロゴ生成モーダル（候補は実際のバナーカラー上で tint プレビュー） */}
      {showLogoAiModal && projectId && (
        <AiImageGeneratorModal
          scene="image_project_logo"
          projectId={projectId}
          candidateClassName={`banner-color${selectedColor}`}
          candidateDisplay="mask"
          onSelect={handleLogoGenerated}
          onClose={() => setShowLogoAiModal(false)}
        />
      )}
    </div>
  );
};

export default ProjectBanner;
